"use client"

import { createContext, useContext, useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import { useAuth } from "@/hooks/useAuth"

type PlanAttentionReason = "OK" | "UNSEEN" | "CHANGED" | "EMPTY" | "TOO_EARLY"

type PlanStatus = {
  day: string
  hasPlan: boolean
  planUpdatedAt: string | null
  seenAt: string | null
  seenPlanUpdatedAt: string | null
  needsAttention: boolean
  reason: PlanAttentionReason
}

type Ctx = {
  status: PlanStatus | null
  refresh: () => Promise<void>
}

const DayPlanAttentionContext = createContext<Ctx | null>(null)

function todayPLString() {
  const now = new Date()
  const yyyy = now.getFullYear()
  const mm = String(now.getMonth() + 1).padStart(2, "0")
  const dd = String(now.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

type TodayPlanItem = {
  id: string
  type: "task" | "custom"
  title: string
  description?: string | null
  projectNo?: string | null
  clientName?: string | null
  clientImageUrl?: string | null
  createdBy?: { id: string; username: string; avatarUrl: string | null } | null
}

export function DayPlanAttentionProvider({ children }: { children: React.ReactNode }) {
  const { data: session } = useAuth()
  const [status, setStatus] = useState<PlanStatus | null>(null)
  const [screenOpen, setScreenOpen] = useState(false)
  const [isMarking, setIsMarking] = useState(false)
  const [items, setItems] = useState<TodayPlanItem[]>([])

  const day = useMemo(() => todayPLString(), [])

  const refresh = async () => {
    try {
      const res = await fetch(`/api/assignments/plan-status?day=${encodeURIComponent(day)}`, { cache: "no-store" })
      if (!res.ok) return
      const data = (await res.json()) as PlanStatus
      setStatus(data)
    } catch {
      // silent
    }
  }

  useEffect(() => {
    void refresh()
    // Polling to catch mid-day updates. Keep it light.
    const t = window.setInterval(() => {
      void refresh()
    }, 90_000)
    return () => window.clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!status) return
    // Hard block: never show if plan is empty.
    if (!status.hasPlan || status.reason === "EMPTY") {
      setScreenOpen(false)
      return
    }

    setScreenOpen(status.reason === "UNSEEN" || status.reason === "CHANGED")
  }, [status])

  useEffect(() => {
    if (!screenOpen) return
    if (!status?.hasPlan) return

    let cancelled = false
    const run = async () => {
      try {
        const res = await fetch(`/api/admin/assignments?day=${encodeURIComponent(day)}`, { cache: "no-store" })
        if (!res.ok) return
        const data = (await res.json()) as {
          day: string
          users: Array<{
            id: string
            taskAssignments: Array<{
              id: string
              order: number
              createdBy?: { id: string; username: string; avatarUrl: string | null } | null
              task: {
                id: string
                title: string
                description?: string | null
                projectNumber: string | null
                client: { id: string; name: string } | null
                clientImageUrl: string | null
              }
            }>
            customAssignments: Array<{
              id: string
              title: string
              order: number
              createdBy?: { id: string; username: string; avatarUrl: string | null } | null
            }>
          }>
        }

        const me = data.users.find((u) => u.id === session?.user?.id)
        if (!me) {
          if (!cancelled) setItems([])
          return
        }

        const merged: TodayPlanItem[] = [
          ...me.customAssignments
            .slice()
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map((c) => ({
              id: `custom-${c.id}`,
              type: "custom" as const,
              title: c.title,
              createdBy: c.createdBy || null,
            })),
          ...me.taskAssignments
            .slice()
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map((a) => ({
              id: a.task.id,
              type: "task" as const,
              title: a.task.title,
              description: a.task.description ?? null,
              projectNo: a.task.projectNumber,
              clientName: a.task.client?.name ?? null,
              clientImageUrl: a.task.clientImageUrl,
              createdBy: a.createdBy || null,
            })),
        ]

        if (!cancelled) setItems(merged)
      } catch {
        if (!cancelled) setItems([])
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [day, screenOpen, session?.user?.id, status?.hasPlan])

  const markRead = async () => {
    if (!status) return
    try {
      setIsMarking(true)
      const res = await fetch("/api/assignments/mark-read", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ day: status.day }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      await refresh()
      setScreenOpen(false)
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Nie udało się oznaczyć jako przeczytane"
      toast.error(msg)
    } finally {
      setIsMarking(false)
    }
  }

  const username = session?.user?.username
  const subtitle = status?.reason === "CHANGED" ? "Twoje dzisiejsze przydziały uległy zmianie" : "Oto twoje dzisiejsze przydziały. Zapoznaj się z nimi, by wiedzieć co masz robić"

  return (
    <DayPlanAttentionContext.Provider value={{ status, refresh }}>
      {children}

      {screenOpen && status?.hasPlan && (
        <div className="fixed inset-0 z-[60] bg-zinc-950 text-white">
          <div className="h-full flex flex-col">
            <div className="px-5 pt-8 pb-4">
              <div className="text-2xl sm:text-3xl font-bold tracking-tight">
                Hej{username ? ", " : ""}{username ? <span className="text-white/90">{username}</span> : ""}
              </div>
              <div className="mt-2 text-sm text-white/60 whitespace-normal break-words">{subtitle}</div>
              <div className="mt-3 text-xs text-white/45">Dzień: {status.day}</div>
            </div>

            <div className="flex-1 overflow-y-auto px-5 pb-28">
              <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 overflow-hidden">
                <div className="px-4 py-3 border-b border-white/10 text-sm font-semibold text-white/85">Przydziały</div>
                <div className="p-4 space-y-2">
                  {items.length === 0 ? (
                    <div className="text-sm text-white/60">Ładowanie…</div>
                  ) : (
                    items.map((it) => (
                      <div key={it.id} className="rounded-xl bg-black/20 ring-1 ring-white/5 p-3.5 space-y-1.5">
                        <div className="flex items-start justify-between gap-2 min-w-0">
                          <div className="text-sm text-white/90 font-medium whitespace-normal break-words flex-1">{it.title}</div>
                          {it.type === "task" && it.projectNo ? (
                            <span className="shrink-0 text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-200 ring-1 ring-emerald-500/25">
                              {it.projectNo}
                            </span>
                          ) : null}
                        </div>
                        {it.type === "task" && !!it.description?.trim() ? (
                          <div className="text-xs text-white/55 line-clamp-2 whitespace-normal break-words">{it.description}</div>
                        ) : null}
                        <div className="pt-1 flex flex-wrap items-center gap-2 text-xs text-white/55">
                          {it.type === "task" && it.clientName ? (
                            <div className="inline-flex items-center gap-1.5 min-w-0">
                              {it.clientImageUrl ? (
                                // eslint-disable-next-line @next/next/no-img-element
                                <img src={it.clientImageUrl} alt="" className="w-4 h-4 object-contain" />
                              ) : (
                                <span className="w-4 h-4 rounded bg-white/10 ring-1 ring-white/10" />
                              )}
                              <span className="truncate">{it.clientName}</span>
                            </div>
                          ) : null}
                          {it.createdBy && (
                            <div className="inline-flex items-center gap-1.5 bg-white/10 px-2 py-0.5 rounded-full ring-1 ring-white/10 text-white/85">
                              <span className="text-[10px] text-white/60">Przypisał:</span>
                              <span className="relative flex h-4 w-4 shrink-0 overflow-hidden rounded-full border border-white/20">
                                {it.createdBy.avatarUrl ? (
                                  <img src={it.createdBy.avatarUrl} alt={it.createdBy.username} className="object-cover w-full h-full" />
                                ) : (
                                  <span className="flex size-full items-center justify-center rounded-full bg-blue-600 text-white text-[8px] font-bold">
                                    {it.createdBy.username.charAt(0).toUpperCase()}
                                  </span>
                                )}
                              </span>
                              <span className="font-semibold text-xs text-white/95">
                                {it.createdBy.username.charAt(0).toUpperCase() + it.createdBy.username.slice(1)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="fixed left-0 right-0 bottom-0 p-4 bg-gradient-to-t from-zinc-950 via-zinc-950/95 to-zinc-950/0">
              <button
                className={
                  "w-full px-5 py-4 rounded-2xl font-semibold text-base transition-colors ring-1 " +
                  (isMarking
                    ? "bg-blue-600/60 text-white/80 ring-blue-500/25"
                    : "bg-blue-600 hover:bg-blue-500 text-white ring-blue-500/25")
                }
                onClick={() => void markRead()}
                disabled={isMarking}
              >
                {isMarking ? "Zapisywanie…" : "Zapoznałem się z planem"}
              </button>
            </div>
          </div>
        </div>
      )}
    </DayPlanAttentionContext.Provider>
  )
}

export function useDayPlanAttention() {
  const ctx = useContext(DayPlanAttentionContext)
  if (!ctx) throw new Error("useDayPlanAttention must be used within DayPlanAttentionProvider")
  return ctx
}
